import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Divider,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useToast } from '../context/ToastContext';
import {
  useGetTeamsQuery,
  useDeleteTeamMutation,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useGenerateTeamsMutation,
} from '../redux/services/chatwootTeamsApi';
import {DATE_COMPARISON_LABELS, DATE_COMPARISON_COM_LABELS, 
  TRI_STATE, TRI_STATE_LABELS} from '../contants';

// Local storage keys
const STORAGE_KEY = 'teamsFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      pageSize: 100
    };
  } catch (e) {
    return {
      pageSize: 100
    };
  }
};

const ChatwootTeams = () => {
  const { showToast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  
  // Get saved filters on component mount
  const initialFilters = getSavedFilters();
  
  // Pagination state with localStorage initialization
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    return initialFilters.pageSize || 100;
  });
  
  // Loading states
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  
  // Pagination model for DataGrid
  const [paginationModel, setPaginationModel] = useState({
    pageSize: pageSize,
    page: page,
  });

  // Save filters to localStorage when they change
  useEffect(() => {
    const filters = {
      pageSize: pageSize
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [pageSize]);

  const {
    data: teams,
    isLoading: isTeamsLoading,
    error: teamsError,
    refetch,
  } = useGetTeamsQuery({});

  const [deleteTeam] = useDeleteTeamMutation();
  const [createTeam, { isLoading: isCreating }] = useCreateTeamMutation();
  const [updateTeam, { isLoading: isUpdating }] = useUpdateTeamMutation();
  const [generateTeams, { isLoading: isGeneratingTeams }] = useGenerateTeamsMutation();

  // Safely extract the array of teams from the API response
  const teamsArray = useMemo(() => {
    if (!teams) return [];
    
    // Check if teams is an array or has a data property
    let dataArray = [];
    if (Array.isArray(teams)) {
      dataArray = teams;
    } else if (teams.items && Array.isArray(teams.items)) {
      dataArray = teams.items;
    }
    
    // Filter out undefined items and ensure each item has an id
    return dataArray.filter(item => item && typeof item === 'object').map(item => {
      if (item.id === undefined || item.id === null) {
        return { ...item, id: `temp-id-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
  }, [teams]);

  const [teamForm, setTeamForm] = useState({
    title: '',
    team_id: 0,
    priority: 0,
    last_marafon: TRI_STATE.NA,
    on_marafon: '',
    time_from: '',
    time_to: '',
    web1: 0,
    web2: 0,
    web_faq: TRI_STATE.NA,
    cond: 'AND',
    is_sale: 0,
    is_order: 0,
    is_prepay: 0,
    escalate_to: '',
    cur_date: 0,
    plus_days: '',
    cur_date_to: 0,
    plus_days_to: '',
    is_default: false,
  });

  const handleDeleteClick = (team) => {
    setSelectedTeam(team);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTeam(selectedTeam.id).unwrap();
      showToast('Team deleted successfully', 'success');
      refetch();
    } catch (err) {
      showToast(
        err.data?.message || 'Failed to delete team', 
        'error'
      );
    } finally {
      setDeleteDialogOpen(false);
      setSelectedTeam(null);
    }
  };

  // Handle pagination model change (page size and current page)
  const handlePaginationModelChange = async (model) => {
    setIsPaginationLoading(true);
    setPaginationModel(model);
    setPage(model.page);
    setPageSize(model.pageSize);
    
    // Save to localStorage
    const filters = {
      ...getSavedFilters(),
      pageSize: model.pageSize
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    
    try {
      // Only refetch if your API supports server-side pagination
      // If you're doing client-side pagination, you can skip this
      // await refetch();
      await new Promise(resolve => setTimeout(resolve, 100));
    } finally {
      setIsPaginationLoading(false);
    }
  };

  const isLoading = isTeamsLoading || isCreating || isUpdating || isPaginationLoading || isGeneratingTeams;

  // Only show loading state if we're fetching the initial data
  if (isTeamsLoading && !teams) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading teams...</Typography>
      </Box>
    );
  }

  // Add additional debugging for the error state
  if (teamsError) {
    return (
      <Typography variant="h4" color="error" sx={{ textAlign: 'center', mt: 4 }}>
        Error loading teams: {teamsError.status} {teamsError.data?.message || 'Unknown error'}
      </Typography>
    );
  }

  // Define DataGrid columns with better error handling
  const columns = [
    { 
      field: 'title', 
      headerName: 'Title', 
      width: 150,
      flex: 1,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    { 
      field: 'is_default',
      headerName: 'Default',
      width: 80,
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        return params.row.is_default ? 'Yes' : 'No';
      },
    },
    { 
      field: 'team_id', 
      headerName: 'Chatwoot Team ID', 
      width: 120,
      flex: 1,
      valueGetter: (params) => {
        if (!params) return '';
        return params || '';
      },
    },
    { 
      field: 'priority', 
      headerName: 'Priority', 
      width: 80,
      valueGetter: (params) => {
        if (!params) return 0;
        return params || 0;
      },
    },
    { 
      field: 'last_marafon', 
      headerName: 'Last Marathon', 
      width: 100,
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        return TRI_STATE_LABELS[params.row.last_marafon ?? 0];
      },
    },
    { 
      field: 'cur_date', 
      headerName: 'Cur. Date', 
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        
        const dateCompValue = params.row.cur_date || params.row.date_comparison;
        const dateCompValueTo = params.row.cur_date_to;
        
        const ranges = [];
        
        // First date range
        if (dateCompValue && dateCompValue !== 0) {
          const label = DATE_COMPARISON_COM_LABELS[dateCompValue] || `Value ${dateCompValue}`;
          const plusDays = params.row.plus_days && params.row.plus_days !== '0' ? params.row.plus_days : '';
          ranges.push(`${label}${plusDays !== '' ? `+${plusDays}` : ''}`);
        }
        
        // Second date range
        if (dateCompValueTo && dateCompValueTo !== 0) {
          const labelTo = DATE_COMPARISON_COM_LABELS[dateCompValueTo] || `Value ${dateCompValueTo}`;
          const plusDaysTo = params.row.plus_days_to && params.row.plus_days_to !== '0' ? params.row.plus_days_to : '';
          ranges.push(`${labelTo}${plusDaysTo !== '' ? `+${plusDaysTo}` : ''}`);
        }
        
        if (ranges.length === 0) return '-';
        if (ranges.length === 1) return ranges[0];
        
        return (
          <div style={{ fontSize: '0.875rem', lineHeight: '1.2' }}>
            <div>{ranges[0]}</div>
            <div style={{ color: '#666', fontWeight: 'bold', fontSize: '0.75rem' }}>AND</div>
            <div>{ranges[1]}</div>
          </div>
        );
      },
    },
    { 
      field: 'time_window', 
      headerName: 'Time Window', 
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row || (!params.row.time_from && !params.row.time_to)) return '-';
        return `${params.row.time_from || '-'} to ${params.row.time_to || '-'}`;
      },
    },
    { 
      field: 'web', 
      headerName: 'Web Settings', 
      width: 160,
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        
        const webs = [];
        
        // Add Web1 flag
        if (params.row.web1 === TRI_STATE.YES) {
          webs.push(<span key="web1" style={{color: 'green', marginRight: '4px'}}>Web1</span>);
        } else if (params.row.web1 === TRI_STATE.NO) {
          webs.push(<span key="web1" style={{color: 'red', marginRight: '4px'}}>Web1</span>);
        }
        
        // Add Web2 flag
        if (params.row.web2 === TRI_STATE.YES) {
          webs.push(<span key="web2" style={{color: 'green', marginRight: '4px'}}>Web2</span>);
        } else if (params.row.web2 === TRI_STATE.NO) {
          webs.push(<span key="web2" style={{color: 'red', marginRight: '4px'}}>Web2</span>);
        }
        
        // Add Web FAQ flag
        if (params.row.web_faq === TRI_STATE.YES) {
          webs.push(<span key="web_faq" style={{color: 'green', marginRight: '4px'}}>WebFAQ</span>);
        } else if (params.row.web_faq === TRI_STATE.NO) {
          webs.push(<span key="web_faq" style={{color: 'red', marginRight: '4px'}}>WebFAQ</span>);
        }
        
        return webs.length > 0 ? webs : '-';
      },
    },
    { 
      field: 'flags', 
      headerName: 'Flags', 
      width: 150,
      renderCell: (params) => {
        if (!params || !params.row) return '-';
        
        const flags = [];
         
        // Add Order flag
        if (params.row.is_order === TRI_STATE.YES) {
          flags.push(<span key="order" style={{color: 'green', marginRight: '4px'}}>Order</span>);
        } else if (params.row.is_order === TRI_STATE.NO) {
          flags.push(<span key="order" style={{color: 'red', marginRight: '4px'}}>Order</span>);
        }
        
        // Add Sale flag
        if (params.row.is_sale === TRI_STATE.YES) {
          flags.push(<span key="sale" style={{color: 'green', marginRight: '4px'}}>Sale</span>);
        } else if (params.row.is_sale === TRI_STATE.NO) {
          flags.push(<span key="sale" style={{color: 'red', marginRight: '4px'}}>Sale</span>);
        }
        
        // Add Prepay flag
        if (params.row.is_prepay === TRI_STATE.YES) {
          flags.push(<span key="prepay" style={{color: 'green', marginRight: '4px'}}>Prepay</span>);
        } else if (params.row.is_prepay === TRI_STATE.NO) {
          flags.push(<span key="prepay" style={{color: 'red', marginRight: '4px'}}>Prepay</span>);
        }
        
        // Add Escalate To
        if (params.row.escalate_to) {
          flags.push(<span key="escalate_to" style={{color: 'green', marginRight: '4px'}}>{params.row.escalate_to}</span>);
        }
        
        return flags.length > 0 ? flags : '-';
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        return (
          <Box>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleEditClick(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteClick(params.row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for plus_days and plus_days_to
    if (name === 'plus_days' || name === 'plus_days_to') {
      // Remove any spaces and non-numeric/non-comma characters
      const cleanValue = value.replace(/[^\d,]/g, '');
      setTeamForm(prev => ({
        ...prev,
        [name]: cleanValue
      }));
      return;
    }
    
    // Special handling for team_id
    if (name === 'team_id') {
      setTeamForm(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : 0
      }));
      return;
    }
    
    // Special handling for priority
    if (name === 'priority') {
      setTeamForm(prev => ({
        ...prev,
        [name]: value ? parseInt(value, 10) : 0
      }));
      return;
    }
    
    // Format time input fields
    if ((name === 'time_from' || name === 'time_to') && value) {
      // Allow only numbers and colon
      let formattedValue = value.replace(/[^\d:]/g, '');
      
      // Format as HH:MM
      if (formattedValue.length > 0) {
        // Remove any existing colons
        formattedValue = formattedValue.replace(/:/g, '');
        
        if (formattedValue.length > 2) {
          // Insert colon after the first two digits (hours)
          formattedValue = `${formattedValue.substring(0, 2)}:${formattedValue.substring(2, 4)}`;
        }
        
        // Validate hours (0-23) and minutes (0-59)
        const parts = formattedValue.split(':');
        if (parts[0] && parseInt(parts[0]) > 23) {
          parts[0] = '23';
        }
        if (parts[1] && parseInt(parts[1]) > 59) {
          parts[1] = '59';
        }
        
        formattedValue = parts.join(':');
      }
      
      setTeamForm(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      return;
    }
    
    setTeamForm(prev => ({
      ...prev,
      [name]: value,
      // Clear related fields if last_marafon is set to NO or N/A
      ...(name === 'last_marafon' && (value === TRI_STATE.NO.toString() || value === TRI_STATE.NA.toString()) ? {
        cur_date: 0,
        plus_days: '',
        cur_date_to: 0,
        plus_days_to: '',
        time_from: '',
        time_to: ''
      } : {})
    }));
  };

  const handleEditClick = (team) => {
    setSelectedTeam(team);
    setTeamForm({
      title: team.title || '',
      team_id: team.team_id ? parseInt(team.team_id, 10) : 0,
      priority: team.priority || 0,
      last_marafon: team.last_marafon ?? TRI_STATE.NA,
      on_marafon: team.on_marafon || '',
      time_from: team.time_from || '',
      time_to: team.time_to || '',
      web1: team.web1 ?? TRI_STATE.NA,
      web2: team.web2 ?? TRI_STATE.NA,
      web_faq: team.web_faq ?? TRI_STATE.NA,
      cond: team.cond || 'AND',
      is_sale: team.is_sale ?? TRI_STATE.NA,
      is_order: team.is_order ?? TRI_STATE.NA,
      is_prepay: team.is_prepay ?? TRI_STATE.NA,
      escalate_to: team.escalate_to ?? '',
      cur_date: team.cur_date || team.date_comparison || 0,
      plus_days: team.plus_days || '',
      cur_date_to: team.cur_date_to || 0,
      plus_days_to: team.plus_days_to || '',
      is_default: team.is_default || false,
    });
    setEditDialogOpen(true);
  };

  const handleCreateClick = () => {
    setTeamForm({
      title: '',
      team_id: 0,
      priority: 0,
      last_marafon: TRI_STATE.NA,
      on_marafon: '',
      time_from: '',
      time_to: '',
      web1: TRI_STATE.NA,
      web2: TRI_STATE.NA,
      web_faq: TRI_STATE.NA,
      cond: 'AND',
      is_sale: TRI_STATE.NA,
      is_order: TRI_STATE.NA,
      is_prepay: TRI_STATE.NA,
      escalate_to: '',
      cur_date: TRI_STATE.NA,
      plus_days: '',
      cur_date_to: TRI_STATE.NA,
      plus_days_to: '',
      is_default: false,
    });
    setCreateDialogOpen(true);
  };

  const handlePlusDaysKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentValue = String(teamForm.plus_days || '');
      const lastNumber = currentValue.split(',').pop();
      
      if (lastNumber) {
        setTeamForm(prev => ({
          ...prev,
          plus_days: currentValue + (currentValue.endsWith(',') ? '' : ',')
        }));
      }
    }
  };

  const handlePlusDaysToKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const currentValue = String(teamForm.plus_days_to || '');
      const lastNumber = currentValue.split(',').pop();
      
      if (lastNumber) {
        setTeamForm(prev => ({
          ...prev,
          plus_days_to: currentValue + (currentValue.endsWith(',') ? '' : ',')
        }));
      }
    }
  };

  const handleCreateTeam = async () => {
    try {
      if (!teamForm.team_id) {
        showToast('Chatwoot Team ID is required', 'error');
        return;
      }
      
      const teamData = {
        ...teamForm,
        priority: parseInt(teamForm.priority, 10) || 0,
        cur_date: parseInt(teamForm.cur_date, 10),
        plus_days: teamForm.plus_days.replace(/,+$/, ''),
        cur_date_to: parseInt(teamForm.cur_date_to, 10),
        plus_days_to: teamForm.plus_days_to.replace(/,+$/, ''),
        on_marafon: teamForm.on_marafon || '',
        last_marafon: isNaN(parseInt(teamForm.last_marafon, 10)) ? TRI_STATE.NA : parseInt(teamForm.last_marafon, 10),
        web1: isNaN(parseInt(teamForm.web1, 10)) ? TRI_STATE.NA : parseInt(teamForm.web1, 10),
        web2: isNaN(parseInt(teamForm.web2, 10)) ? TRI_STATE.NA : parseInt(teamForm.web2, 10),
        web_faq: isNaN(parseInt(teamForm.web_faq, 10)) ? TRI_STATE.NA : parseInt(teamForm.web_faq, 10),
        is_sale: isNaN(parseInt(teamForm.is_sale, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_sale, 10),
        is_order: isNaN(parseInt(teamForm.is_order, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_order, 10),
        is_prepay: isNaN(parseInt(teamForm.is_prepay, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_prepay, 10),
        escalate_to: teamForm.escalate_to || '',
      };
      
      await createTeam(teamData).unwrap();
      showToast('Team created successfully', 'success');
      setCreateDialogOpen(false);
      refetch();
    } catch (error) {
      showToast(error.data?.message || 'Failed to create team', 'error');
    }
  };

  const handleUpdateTeam = async () => {
    try {
      if (!teamForm.team_id) {
        showToast('Chatwoot Team ID is required', 'error');
        return;
      }
      
      if (!selectedTeam || !selectedTeam.id) {
        showToast('Cannot update team: Missing ID', 'error');
        return;
      }
      
      const teamData = {
        ...teamForm,
        priority: parseInt(teamForm.priority, 10) || 0,
        cur_date: parseInt(teamForm.cur_date, 10),
        plus_days: String(teamForm.plus_days || '').replace(/,+$/, ''),
        cur_date_to: parseInt(teamForm.cur_date_to, 10),
        plus_days_to: String(teamForm.plus_days_to || '').replace(/,+$/, ''),
        on_marafon: teamForm.on_marafon || '',
        last_marafon: isNaN(parseInt(teamForm.last_marafon, 10)) ? TRI_STATE.NA : parseInt(teamForm.last_marafon, 10),
        web1: isNaN(parseInt(teamForm.web1, 10)) ? TRI_STATE.NA : parseInt(teamForm.web1, 10),
        web2: isNaN(parseInt(teamForm.web2, 10)) ? TRI_STATE.NA : parseInt(teamForm.web2, 10),
        web_faq: isNaN(parseInt(teamForm.web_faq, 10)) ? TRI_STATE.NA : parseInt(teamForm.web_faq, 10),
        is_sale: isNaN(parseInt(teamForm.is_sale, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_sale, 10),
        is_order: isNaN(parseInt(teamForm.is_order, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_order, 10),
        is_prepay: isNaN(parseInt(teamForm.is_prepay, 10)) ? TRI_STATE.NA : parseInt(teamForm.is_prepay, 10),
        escalate_to: teamForm.escalate_to || '',
      };
      
      await updateTeam({ id: selectedTeam.id, ...teamData }).unwrap();
      showToast('Team updated successfully', 'success');
      setEditDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error updating team:', error);
      showToast(error.data?.message || 'Failed to update team', 'error');
    }
  };

  const handleGenerateTeams = async () => {
    try {
      await generateTeams().unwrap();
      showToast('Teams generated successfully', 'success');
      refetch();
    } catch (error) {
      showToast(error.data?.message || 'Failed to generate teams', 'error');
    }
  };

  return (
    <Paper elevation={1} sx={{ width: '100%', borderRadius: 1, p: 2, mb: 2 }}>
      <Box sx={{ width: '100%', p: 2, mb: 2 }}>      
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Chatwoot Teams
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
            >
              Add Team
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleGenerateTeams}
              disabled={isGeneratingTeams}
            >
              {isGeneratingTeams ? 'Generating...' : 'Generate Teams'}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* DataGrid with correct ID handling */}
        <Box sx={{ height: 'calc(97vh - 250px)', width: '100%', mt: 2 }}>
          {teamsArray.length === 0 && !isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <Typography color="text.secondary">No teams found</Typography>
            </Box>
          ) : (
            <DataGrid
              rows={teamsArray}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[100]}
              disableRowSelectionOnClick
              disableColumnMenu
              loading={isLoading}
              getRowId={(row) => {
                if (row.id === undefined || row.id === null) {
                  console.warn('Row missing ID in getRowId:', row);
                  return `temp-${Math.random()}`;
                }
                return row.id;
              }}
              componentsProps={{
                noRowsOverlay: {
                  style: {
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }
                }
              }}
            />
          )}
        </Box>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this team? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Team Form Dialog (shared between edit and create) */}
        <Dialog 
          open={editDialogOpen || createDialogOpen} 
          onClose={() => editDialogOpen ? setEditDialogOpen(false) : setCreateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editDialogOpen ? 'Edit Team' : 'Create New Team'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
              <TextField
                margin="dense"
                label="Title"
                name="title"
                fullWidth
                value={teamForm.title}
                onChange={handleInputChange}
                variant="outlined"
              />
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={teamForm.is_default}
                    onChange={(e) => setTeamForm(prev => ({
                      ...prev,
                      is_default: e.target.checked
                    }))}
                    name="is_default"
                  />
                }
                label="Use as default if other not found"
                sx={{ mt: 1, mb: 1 }}
              />
              
              <TextField
                required
                margin="dense"
                label="Chatwoot Team ID"
                name="team_id"
                fullWidth
                value={teamForm.team_id}
                onChange={handleInputChange}
                variant="outlined"
                error={!teamForm.team_id}
                helperText={!teamForm.team_id ? "Chatwoot Team ID is required" : ""}
              />
              
              <TextField
                margin="dense"
                label="Priority"
                name="priority"
                type="number"
                fullWidth
                value={teamForm.priority}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{
                  min: 0,
                  step: 1
                }}
                helperText="Higher priority teams are processed first (default: 0)"
              />
              
              {<>
                  {/* Last Marathon and On Marathon in one row */}
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1, mb: 1 }}>
                    <TextField
                      select
                      margin="dense"
                      label="Last Marathon"
                      name="last_marafon"
                      value={teamForm.last_marafon}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120, mt: 0 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    
                    {+teamForm.last_marafon === TRI_STATE.YES && <TextField
                      margin="dense"
                      label="On Marathon"
                      name="on_marafon"
                      fullWidth
                      size="small"
                      value={teamForm.on_marafon}
                      onChange={handleInputChange}
                      variant="outlined"
                      sx={{ mt: 0 }}
                    />}
                  </Box>
                  
                  {/* Date comparison selector and plus_days input */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5, alignItems: 'center' }}>
                      <TextField
                        margin="dense"
                        label="Current Date"
                        name="cur_date"
                        select
                        fullWidth
                        value={teamForm.cur_date}
                        onChange={handleInputChange}
                        variant="outlined"
                        SelectProps={{
                          native: true,
                        }}
                        sx={{ mt: 0 }}
                      >
                        {Object.entries(DATE_COMPARISON_LABELS)
                          .filter(([value]) => +teamForm.last_marafon === TRI_STATE.YES || (+teamForm.last_marafon !== TRI_STATE.YES && (parseInt(value) === 0 || (parseInt(value) > 3 && parseInt(value) < 7))))
                          .map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                      </TextField>
                      
                      {teamForm.cur_date !== 0 && (
                        <TextField
                          margin="dense"
                          label="Plus Days"
                          name="plus_days"
                          fullWidth
                          value={teamForm.plus_days}
                          onChange={handleInputChange}
                          onKeyDown={handlePlusDaysKeyDown}
                          variant="outlined"
                          placeholder="Enter numbers, press Enter to add more"
                          helperText="Press Enter after each number to add multiple values"
                          sx={{ mt: 3 }}
                        />
                      )}
                    </Box>
                    
                    <Typography variant="body2" sx={{ textAlign: 'center', my: 0.5, fontWeight: 'bold' }}>
                      AND
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5, alignItems: 'center' }}>
                      <TextField
                        margin="dense"
                        label="Current Date"
                        name="cur_date_to"
                        select
                        fullWidth
                        value={teamForm.cur_date_to}
                        onChange={handleInputChange}
                        variant="outlined"
                        SelectProps={{
                          native: true,
                        }}
                        sx={{ mt: 0 }}
                      >
                        {Object.entries(DATE_COMPARISON_LABELS)
                          .filter(([value]) => +teamForm.last_marafon === TRI_STATE.YES || (+teamForm.last_marafon !== TRI_STATE.YES && (parseInt(value) === 0 || (parseInt(value) > 3 && parseInt(value) < 7))))
                          .map(([value, label]) => (
                            <option key={value} value={parseInt(value)}>
                              {label}
                            </option>
                          ))}
                      </TextField>
                      
                      {teamForm.cur_date_to !== 0 && (
                        <TextField
                          margin="dense"
                          label="Plus Date"
                          name="plus_days_to"
                          fullWidth
                          value={teamForm.plus_days_to}
                          onChange={handleInputChange}
                          onKeyDown={handlePlusDaysToKeyDown}
                          variant="outlined"
                          placeholder="Enter numbers, press Enter to add more"
                          helperText="Press Enter after each number to add multiple values"
                          sx={{ mt: 3 }}
                        />
                      )}
                    </Box>
                
                  <small>The time check works for the date that is equal to the value in the Current Date field.</small>
                  
                  <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 1 }}>
                    <TextField
                      margin="dense"
                      label="Time From"
                      name="time_from"
                      fullWidth
                      value={teamForm.time_from}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="HH:MM"
                      inputProps={{
                        maxLength: 5,
                        pattern: "[0-9]{2}:[0-9]{2}"
                      }}
                      helperText="Format: HH:MM (24-hour)"
                      sx={{ mt: 0 }}
                    />
                    <TextField
                      margin="dense"
                      label="Time To"
                      name="time_to"
                      fullWidth
                      value={teamForm.time_to}
                      onChange={handleInputChange}
                      variant="outlined"
                      placeholder="HH:MM"
                      inputProps={{
                        maxLength: 5,
                        pattern: "[0-9]{2}:[0-9]{2}"
                      }}
                      helperText="Format: HH:MM (24-hour)"
                      sx={{ mt: 0 }}
                    />
                  </Box>
                
                  {/* All selects in one row */}
                  <p><b>AND</b></p>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                    <TextField
                      select
                      label="Web1"
                      name="web1"
                      value={teamForm.web1}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    
                    <TextField
                      select
                      label="Condition"
                      name="cond"
                      value={teamForm.cond}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 80 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </TextField>
                    
                    <TextField
                      select
                      label="Web2"
                      name="web2"
                      value={teamForm.web2}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    
                    <TextField
                      select
                      label="Condition"
                      name="cond"
                      value={teamForm.cond}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 80 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      <option value="AND">AND</option>
                      <option value="OR">OR</option>
                    </TextField>
                    
                    <TextField
                      select
                      label="WebFAQ"
                      name="web_faq"
                      value={teamForm.web_faq}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                  </Box>
                  
                  {/* Order, Sale, Prepay selects in one row */}
                  <p><b>AND</b></p>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, mt: 1 }}>
                    <TextField
                      select
                      label="Order"
                      name="is_order"
                      value={teamForm.is_order}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Sale"
                      name="is_sale"
                      value={teamForm.is_sale}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Prepay"
                      name="is_prepay"
                      value={teamForm.is_prepay}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      {Object.entries(TRI_STATE_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Escalate To"
                      name="escalate_to"
                      value={teamForm.escalate_to}
                      onChange={handleInputChange}
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 120 }}
                      SelectProps={{
                        native: true
                      }}
                    >
                      <option value="">N/A</option>
                      <option value="clients">Clients</option>
                      <option value="sales">Sales</option>
                    </TextField>
                  </Box>
                </>
              }
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => editDialogOpen ? setEditDialogOpen(false) : setCreateDialogOpen(false)} 
              color="primary"
            >
              Cancel
            </Button>
            <Button 
              onClick={editDialogOpen ? handleUpdateTeam : handleCreateTeam} 
              color="primary" 
              variant="contained"
            >
              {editDialogOpen ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Paper>
  );
};

export default ChatwootTeams; 